import React from 'react';
import { View, StyleSheet, TouchableOpacity } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { useNavigation } from '@react-navigation/native';
import { useTheme } from '../../theme/ThemeContext';
import ThemedText from '../common/ThemedText';

interface Notification {
  id: string;
  type: string;
  title: string;
  message: string;
  read: boolean;
  createdAt: string;
}

interface ActivityCardProps {
  notifications: Notification[];
}

const ActivityCard: React.FC<ActivityCardProps> = ({ notifications }) => {
  const { theme } = useTheme();
  const navigation = useNavigation<any>();

  const handlePress = () => {
    navigation.navigate('Notification');
  };

  const getNotificationIcon = (type: string) => {
    switch (type) {
      case 'connection_request':
        return 'person-add';
      case 'connection_accepted':
        return 'checkmark-circle';
      case 'ledger_entry':
        return 'wallet';
      case 'work_quote':
        return 'briefcase';
      case 'club_invitation':
        return 'people';
      case 'message':
        return 'chatbubble';
      default:
        return 'notifications';
    }
  };

  const formatTime = (timestamp: string) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diffInMs = now.getTime() - date.getTime();
    const diffInMinutes = Math.floor(diffInMs / 60000);
    const diffInHours = Math.floor(diffInMinutes / 60);
    const diffInDays = Math.floor(diffInHours / 24);

    if (diffInMinutes < 1) return 'Just now';
    if (diffInMinutes < 60) return `${diffInMinutes}m ago`;
    if (diffInHours < 24) return `${diffInHours}h ago`;
    if (diffInDays < 7) return `${diffInDays}d ago`;
    return date.toLocaleDateString();
  };

  const displayNotifications = notifications.slice(0, 3);

  if (notifications.length === 0) {
    return null;
  }

  return (
    <TouchableOpacity 
      style={[styles.card, { backgroundColor: theme.card, borderColor: theme.border }]}
      onPress={handlePress}
      activeOpacity={0.7}
    >
      <View style={styles.header}>
        <View style={[styles.iconContainer, { backgroundColor: theme.info + '20' }]}>
          <Ionicons name="notifications-outline" size={24} color={theme.info} />
        </View>
        <ThemedText style={[styles.title, { color: theme.text }]}>
          Recent Activity
        </ThemedText>
        <Ionicons name="chevron-forward" size={20} color={theme.textTertiary} />
      </View>

      <View style={styles.notificationsList}>
        {displayNotifications.map((notification, index) => (
          <View 
            key={notification.id}
            style={[
              styles.notificationItem,
              index < displayNotifications.length - 1 && { borderBottomWidth: 1, borderBottomColor: theme.borderLight }
            ]}
          >
            <View style={[styles.notificationIcon, { backgroundColor: notification.read ? theme.backgroundTertiary : theme.info + '15' }]}>
              <Ionicons 
                name={getNotificationIcon(notification.type)} 
                size={18} 
                color={notification.read ? theme.textTertiary : theme.info} 
              />
            </View>
            <View style={styles.notificationContent}>
              <ThemedText 
                style={[styles.notificationTitle, { color: theme.text }]} 
                numberOfLines={1}
              >
                {notification.title}
              </ThemedText>
              <ThemedText 
                style={[styles.notificationMessage, { color: theme.textSecondary }]} 
                numberOfLines={1}
              >
                {notification.message}
              </ThemedText>
            </View>
            <View style={styles.notificationMeta}>
              <ThemedText style={[styles.notificationTime, { color: theme.textTertiary }]}>
                {formatTime(notification.createdAt)}
              </ThemedText>
              {!notification.read && (
                <View style={[styles.unreadDot, { backgroundColor: theme.info }]} />
              )}
            </View>
          </View>
        ))}
      </View>

      {notifications.length > 3 && (
        <View style={[styles.footer, { borderTopColor: theme.borderLight }]}>
          <ThemedText style={[styles.viewAllText, { color: theme.accent }]}>
            View all notifications
          </ThemedText>
        </View>
      )}
    </TouchableOpacity>
  );
};

const styles = StyleSheet.create({
  card: {
    borderRadius: 16,
    padding: 16,
    marginBottom: 16,
    borderWidth: 1,
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 16,
  },
  iconContainer: {
    width: 40,
    height: 40,
    borderRadius: 12,
    justifyContent: 'center',
    alignItems: 'center',
    marginRight: 12,
  },
  title: {
    fontSize: 18,
    fontWeight: '600',
    flex: 1,
  },
  notificationsList: {
    gap: 0,
  },
  notificationItem: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    paddingVertical: 12,
  },
  notificationIcon: {
    width: 36,
    height: 36,
    borderRadius: 18,
    justifyContent: 'center',
    alignItems: 'center',
    marginRight: 12,
  },
  notificationContent: {
    flex: 1,
    marginRight: 8,
  },
  notificationTitle: {
    fontSize: 14,
    fontWeight: '600',
    marginBottom: 2,
  },
  notificationMessage: {
    fontSize: 13,
  },
  notificationMeta: {
    alignItems: 'flex-end',
  },
  notificationTime: {
    fontSize: 11,
    marginBottom: 4,
  },
  unreadDot: {
    width: 8,
    height: 8,
    borderRadius: 4,
  },
  footer: {
    marginTop: 8,
    paddingTop: 12,
    borderTopWidth: 1,
    alignItems: 'center',
  },
  viewAllText: {
    fontSize: 14,
    fontWeight: '600',
  },
});

export default ActivityCard;
